import { Global } from '@emotion/react';
import { styleSkeleton } from '@inflearn/app-shell/dist/skeleton';
import { LANGUAGES } from '@inflearn/i18n-tools';
import { TranslationEditor } from '@inflearn/translation-editor';
import { config as faConfig } from '@inflearn/fontawesome-svg-core';
import type { i18n as I18nInstance } from 'i18next';
import type { NextPage } from 'next';
import { appWithTranslation, useTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';

import { NEXT_PUBLIC_COMMIT_HASH, NEXT_PUBLIC_USE_MSW, NEXT_PUBLIC_VERSION } from '../../appConfig';
import { initMocks } from '../../mocks';
import { AnalyticsProvider } from '../providers/AnalyticsProvider';
import { LoginModalProvider } from '../providers/ControlLoginModalContextProvider';
import { DsProvider } from '../providers/DsProvider';
import { QueryClientProvider } from '../providers/QueryClientProvider';

import { HackleProvider } from '@inflearn/hackle';
import '@inflearn/ds-image-cropper-next/dist/index.css';
import '@inflearn/ds-markdown-body-next/styles.css';
import '@inflearn/editor-next/dist/style.css';
import '@inflearn/fontawesome-svg-core/styles.css';
import '@inflearn/ds-table-next/ag-grid.css';
// -next의 styles.css가 v6의 fontawesome-pro-solid.css(필터·정렬 아이콘 폰트)를 포함한다
import '@inflearn/ds-table-next/styles.css';
import '@inflearn/ds-dates-next/styles.css';
import '@inflearn/ds-carousel-next/styles.css';
import '../styles/globals.css';
import '../styles/markdown-body.css';
import '@inflearn/ds-react-next/mantine-core-styles.css';

import '@inflearn/ds-react-next/mantine-notifications-styles.css';

import dynamic from 'next/dynamic';
import nextI18NextConfig from '../../next-i18next.config';
import { initMonitoring } from '../components/Monitoring/initMonitoring';
import SetUserToThirdParty from '../components/common/SetUserToThirdParty';
import { useI18nCdnOverride } from '../hooks/useI18nCdnOverride';
import { useInAppBrowserBypass } from '../hooks/useInAppBrowserBypass';
import { useSetLocales } from '../hooks/useSetLocales';
import {
  IS_CDN_ENABLED,
  IS_TRANSLATION_EDITOR_ENABLED,
  LOCIZE_API_KEY,
  LOCIZE_PROJECT_ID,
} from '../utils/i18n/config';
import { createLocizeBackend } from '../utils/i18n/createLocizeBackend';
import { SUBLIMINAL_INIT_OPTIONS, registerSubliminal } from '../utils/i18n/subliminal';

const Monitoring = dynamic(
  () => import('../components/Monitoring/Monitoring').then((mod) => mod.Monitoring),
  {
    ssr: false,
  },
);

faConfig.autoAddCss = false;

if (NEXT_PUBLIC_USE_MSW === 'true') {
  await initMocks();
}

if (typeof window !== 'undefined' && NEXT_PUBLIC_COMMIT_HASH) {
  window.__SHA__ = NEXT_PUBLIC_COMMIT_HASH;
}

if (typeof window !== 'undefined' && NEXT_PUBLIC_VERSION) {
  window.__RELEASE__ = NEXT_PUBLIC_VERSION;
}

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pathname: string) => ReactElement;
};

type AppPropsWithLayout = AppProps<{ dehydratedState?: unknown }> & {
  Component: NextPageWithLayout;
};

if (typeof window !== 'undefined') {
  await initMonitoring();
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const { i18n } = useTranslation();
  const router = useRouter();
  const pathname = router.pathname;
  const getLayout = Component.getLayout || ((page) => page);
  const { dehydratedState, ...restPageProps } = pageProps;

  useSetLocales();
  useInAppBrowserBypass();
  useI18nCdnOverride();

  return (
    <>
      <QueryClientProvider dehydratedState={dehydratedState}>
        <DsProvider>
          <HackleProvider>
            <AnalyticsProvider>
              <LoginModalProvider>
                <SetUserToThirdParty />
                <Monitoring />
                <Global styles={[styleSkeleton]} />
                {getLayout(<Component {...restPageProps} />, pathname)}
              </LoginModalProvider>
            </AnalyticsProvider>
          </HackleProvider>
        </DsProvider>
      </QueryClientProvider>
      {IS_TRANSLATION_EDITOR_ENABLED && (
        <TranslationEditor
          i18n={i18n}
          projectId={LOCIZE_PROJECT_ID}
          apiKey={LOCIZE_API_KEY}
          languages={LANGUAGES}
        />
      )}
    </>
  );
}

/** bindI18n 기본값에는 loaded 가 없어, 더하지 않으면 재로드 후 리렌더가 일어나지 않는다. */
const i18nConfigOverride =
  IS_CDN_ENABLED || IS_TRANSLATION_EDITOR_ENABLED
    ? {
        ...nextI18NextConfig,
        ...SUBLIMINAL_INIT_OPTIONS,
        react: { useSuspense: false, bindI18n: 'languageChanged loaded' },
        onPreInitI18next: (instance: I18nInstance) => {
          registerSubliminal(instance);

          // 서버의 파일 backend 와 함께 붙이면 locale 경로를 URL 로 해석해 실패한다.
          if (typeof window === 'undefined' || !IS_CDN_ENABLED) {
            return;
          }

          instance.use(createLocizeBackend());
        },
      }
    : null;

export default appWithTranslation(App, i18nConfigOverride);
